import type { PublicPresentation } from "./presentations";

/**
 * Discount summary for ONE presentation, distilled from the per-egg
 * `aptoDiscountByEgg` map the API returns. Keeps UI code free of the
 * map-shape gymnastics.
 */
export interface PresentationDiscountSummary {
  /** True when at least one egg-type in this presentation is on APTO promo. */
  hasDiscount: boolean;
  /**
   * Highest discount fraction across egg-types in the presentation,
   * in (0, 1) (e.g. 0.20 = 20% off). Zero when `hasDiscount` is false.
   * Picked over the average because it is the value most customers will
   * actually pay when they buy a single-color carton (the dominant
   * MENUDEO use-case); the average is reserved for mixed-slot cartons
   * via `computeAveragePercentForSlots`.
   */
  maxPercent: number;
}

/**
 * Discount summary for a SINGLE-color purchase of `presentation` filled
 * exclusively with `eggTypeId`. Resolves to "no discount" when the
 * egg-type is not in the APTO map. Used by the product page selector
 * where the customer has already picked the egg type.
 */
export function summarizeDiscountForEgg(
  presentation: Pick<PublicPresentation, "aptoDiscountByEgg">,
  eggTypeId: string,
): PresentationDiscountSummary {
  const entry = presentation.aptoDiscountByEgg?.[eggTypeId];
  if (!entry || entry.percentage <= 0) {
    return { hasDiscount: false, maxPercent: 0 };
  }
  return { hasDiscount: true, maxPercent: entry.percentage };
}

/**
 * Discount summary for a PRESENTATION as a whole, regardless of which
 * egg-type the customer ends up picking. Used by the catalog grid to
 * decide whether to render the "OFERTA" badge on a product card —
 * we don't know which presentation the customer will pick yet, so any
 * APTO entry in any presentation flips the badge on.
 */
export function summarizeDiscountForPresentation(
  presentation: Pick<PublicPresentation, "aptoDiscountByEgg">,
): PresentationDiscountSummary {
  const map = presentation.aptoDiscountByEgg;
  if (!map) return { hasDiscount: false, maxPercent: 0 };
  let max = 0;
  for (const key of Object.keys(map)) {
    const pct = map[key]?.percentage ?? 0;
    if (pct > max) max = pct;
  }
  if (max <= 0) return { hasDiscount: false, maxPercent: 0 };
  return { hasDiscount: true, maxPercent: max };
}

/**
 * True when at least one presentation in `presentations` has any
 * APTO-discounted egg-type. Used by the catalog card to flip the
 * "OFERTA" badge — the catalog doesn't pick a presentation yet, so
 * any active promo on any presentation is enough to advertise it.
 */
export function anyPresentationOnApto(
  presentations: ReadonlyArray<Pick<PublicPresentation, "aptoDiscountByEgg">>,
): boolean {
  return presentations.some(
    (p) => Object.keys(p.aptoDiscountByEgg ?? {}).length > 0,
  );
}

/**
 * Average discount percentage across an arbitrary mix of slot egg-types
 * (the MENUDEO mix path: customer-built carton). Each slot contributes
 * its egg-type's APTO percentage from `presentation.aptoDiscountByEgg`,
 * or 0 if that egg-type is not currently on promo.
 *
 * Returns a fraction in [0, 1]. Returns 0 when `slots` is empty or
 * none of the slot egg-types are on promo (`hasDiscount: false`).
 *
 * Why average, not max? In a 12-slot carton with 6 APTO + 6 FRESCO
 * eggs, the customer pays a discount on roughly half of the value;
 * showing the max (the APTO percentage) would over-promise. Average
 * matches the actual realized discount on the final line subtotal.
 */
export function computeAveragePercentForSlots(
  presentation: Pick<PublicPresentation, "aptoDiscountByEgg">,
  slots: ReadonlyArray<{ eggTypeId: string }>,
): PresentationDiscountSummary {
  if (slots.length === 0) return { hasDiscount: false, maxPercent: 0 };
  const map = presentation.aptoDiscountByEgg ?? {};
  let totalPct = 0;
  let hadPromo = false;
  for (const slot of slots) {
    const pct = map[slot.eggTypeId]?.percentage ?? 0;
    if (pct > 0) hadPromo = true;
    totalPct += pct;
  }
  if (!hadPromo) return { hasDiscount: false, maxPercent: 0 };
  return { hasDiscount: true, maxPercent: totalPct / slots.length };
}

/**
 * Format a fraction like 0.15 as the user-facing label "-15%". Rounds
 * to the nearest whole percent — sub-percent precision is noise on a
 * marketing badge.
 */
export function formatDiscountBadge(fraction: number): string {
  const pct = Math.round(fraction * 100);
  return `-${pct}%`;
}
